import { motion } from 'framer-motion'
import { usePatterns } from '../hooks/usePatterns'

// Get color intensity based on activity count
function getIntensityColor(count: number, maxCount: number): string {
  if (maxCount === 0) return '#1f2937' // gray-800
  
  const ratio = count / maxCount
  if (ratio < 0.2) return '#1f2937'      // gray-800
  if (ratio < 0.4) return '#065f46'      // emerald-900
  if (ratio < 0.6) return '#059669'      // emerald-600
  if (ratio < 0.8) return '#10b981'      // emerald-500
  return '#34d399'                       // emerald-400
}

// Format date for tooltip
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  })
}

export default function PatternHeatmap() {
  const { patterns, loading, error } = usePatterns({ days: 30 })

  if (loading) {
    return (
      <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
        <div className="flex items-center justify-center h-32">
          <motion.div
            className="w-8 h-8 border-4 border-gray-700 border-t-emerald-500 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
        <div className="flex items-center justify-center h-32 text-red-400">
          <span className="text-sm">Failed to load activity data</span>
        </div>
      </div>
    )
  }

  // Calculate max count for normalization
  const maxCount = Math.max(...patterns.map(p => p.count), 1)

  // Get start date
  const startDate = new Date()
  startDate.setDate(startDate.getDate() - 30)

  // Generate all 30 days
  const allDays: { date: string; count: number }[] = []
  for (let i = 0; i < 30; i++) {
    const date = new Date(startDate)
    date.setDate(date.getDate() + i)
    const dateStr = date.toISOString().split('T')[0]
    const existing = patterns.find(p => p.date === dateStr)
    allDays.push({
      date: dateStr,
      count: existing?.count || 0
    })
  }

  // Group by week
  const weeks: typeof allDays[] = []
  for (let i = 0; i < allDays.length; i += 7) {
    weeks.push(allDays.slice(i, i + 7))
  }

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  return (
    <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Activity Patterns</h3>
        <span className="text-xs text-gray-500">Last 30 days</span>
      </div>

      {/* Heatmap grid */}
      <div className="flex gap-2">
        {/* Day labels */}
        <div className="flex flex-col gap-1 pt-8">
          {dayLabels.map(day => (
            <div key={day} className="h-4 text-xs text-gray-500 w-8">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="flex gap-1">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="flex flex-col gap-1">
              {week.map((day, dayIndex) => {
                const color = getIntensityColor(day.count, maxCount)
                return (
                  <motion.div
                    key={day.date}
                    className="w-4 h-4 rounded-sm cursor-pointer relative group"
                    style={{ backgroundColor: color }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: (weekIndex * 7 + dayIndex) * 0.01 }}
                    whileHover={{ scale: 1.2 }}
                  >
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                      <div>{formatDate(day.date)}</div>
                      <div className="text-emerald-400">{day.count} observations</div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-end gap-2 mt-4">
        <span className="text-xs text-gray-500">Less</span>
        {['#1f2937', '#065f46', '#059669', '#10b981', '#34d399'].map((color, i) => (
          <div
            key={i}
            className="w-4 h-4 rounded-sm"
            style={{ backgroundColor: color }}
          />
        ))}
        <span className="text-xs text-gray-500">More</span>
      </div>

      {/* Stats summary */}
      <div className="flex gap-4 mt-4 pt-4 border-t border-gray-800">
        <div className="text-center">
          <div className="text-2xl font-bold text-white">
            {patterns.reduce((sum, p) => sum + p.count, 0)}
          </div>
          <div className="text-xs text-gray-500">Total</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-emerald-400">
            {Math.round(patterns.reduce((sum, p) => sum + p.count, 0) / (patterns.length || 1))}
          </div>
          <div className="text-xs text-gray-500">Daily avg</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-cyan-400">
            {patterns.filter(p => p.count > 0).length}
          </div>
          <div className="text-xs text-gray-500">Active days</div>
        </div>
      </div>
    </div>
  )
}
